/** @format */

import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { CopyButton } from 'src/components'
import type { Editor } from 'codemirror'
import { Controlled as CodeMirror } from 'react-codemirror2'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/material.css'
import 'codemirror/mode/shell/shell'
import 'codemirror/mode/python/python'
import 'codemirror/mode/javascript/javascript'
import 'codemirror/mode/yaml/yaml'
import 'codemirror/mode/xml/xml'
import 'codemirror/mode/sql/sql'
import 'codemirror/mode/markdown/markdown'

type CodeLanguage =
  | 'shell'
  | 'bash'
  | 'python'
  | 'javascript'
  | 'typescript'
  | 'json'
  | 'yaml'
  | 'xml'
  | 'sql'
  | 'markdown'
  | string

interface CodeDisplayProps {
  title?: string
  content: string
  language?: CodeLanguage
  width?: string | number
  height?: string | number
  copyable?: boolean
  copyContent?: string
  lineNumbers?: boolean
  readOnly?: boolean
  refreshTrigger?: string | number | boolean
  className?: string
}

const resolveMode = (language: CodeLanguage): string => {
  const lang = language.toLowerCase()
  if (lang === 'bash' || lang === 'shell' || lang === 'sh') return 'shell'
  if (lang === 'python' || lang === 'py') return 'python'
  if (lang === 'json') return 'application/json'
  if (lang === 'typescript' || lang === 'ts') return 'text/typescript'
  if (lang === 'javascript' || lang === 'js') return 'javascript'
  if (lang === 'yaml' || lang === 'yml') return 'yaml'
  if (lang === 'xml' || lang === 'html') return 'xml'
  if (lang === 'sql') return 'sql'
  if (lang === 'markdown' || lang === 'md') return 'markdown'
  return 'shell'
}

const toCssSize = (value: string | number): string => {
  if (typeof value === 'number') return `${value}px`
  return value
}

const CodeDisplay: React.FC<CodeDisplayProps> = ({
  title,
  content,
  copyContent,
  language = 'shell',
  width = '100%',
  height = 96,
  copyable = true,
  lineNumbers = false,
  readOnly = true,
  refreshTrigger,
  className
}) => {
  const editorRef = useRef<Editor | null>(null)

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.setValue(content || '')
      editorRef.current.refresh()
    }
  }, [content])

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.refresh()
      setTimeout(() => editorRef.current?.refresh(), 80)
      setTimeout(() => editorRef.current?.refresh(), 220)
    }
  }, [refreshTrigger])

  return (
    <div className={className} style={{ width: toCssSize(width) }}>
      <div className="trace-span-log-editor-header">
        <span className="trace-span-log-editor-title" title={title}>
          {title}
        </span>
        <CopyButton text={copyContent || content} color="#fff" />
      </div>
      <CodeMirror
        value={content}
        onBeforeChange={() => null}
        options={{
          mode: resolveMode(language),
          theme: 'material',
          lineNumbers,
          lineWrapping: true,
          readOnly,
          cursorBlinkRate: -1
        }}
        editorDidMount={editor => {
          editorRef.current = editor
          editor.setSize('100%', toCssSize(height))
          editor.refresh()
          requestAnimationFrame(() => editor.refresh())
          setTimeout(() => editor.refresh(), 0)
        }}
      />
    </div>
  )
}

export default styled(CodeDisplay)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  overflow: hidden;

  .trace-span-log-editor-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    height: 32px;
    padding: 0 12px;
    background: #232324;
  }

  .trace-span-log-editor-title {
    flex: 1;
    min-width: 0;
    margin-right: 12px;
    font-size: 14px;
    font-weight: 500;
    color: #fff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .CodeMirror {
    flex: 1;
    width: 100%;
    border: none;
    border-radius: 0;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 14px;
    line-height: 1.5;
    background-color: #17171a;
  }

  .cm-s-material.CodeMirror,
  .cm-s-material .CodeMirror-gutters {
    background-color: #17171a;
  }

  .CodeMirror-linenumber {
    color: #86909c;
    font-size: 12px;
  }
`
